import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type PropsWithChildren,
} from "react";

import { GameHost, type HostState } from "./gameHost";

const GameContext = createContext<GameHost | null>(null);

export function GameProvider({
  children,
}: PropsWithChildren): React.JSX.Element {
  const [host, setHost] = useState<GameHost | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void GameHost.create()
      .then((created) => {
        if (!active) {
          created.destroy();
          return;
        }
        created.startLoop();
        setHost(created);
      })
      .catch((reason: unknown) => {
        setError(
          reason instanceof Error
            ? reason.message
            : "The chamber could not initialize.",
        );
      });
    return () => {
      active = false;
      host?.destroy();
    };
  }, []);

  if (error !== null)
    return (
      <main className="fatal-screen">
        <p className="eyebrow">SYSTEM FAULT</p>
        <h1>The chamber could not initialize</h1>
        <p>{error}</p>
        <button
          type="button"
          onClick={() => {
            location.reload();
          }}
        >
          Reload application
        </button>
      </main>
    );
  if (host === null)
    return (
      <main className="loading-screen" aria-live="polite">
        <div className="loading-core" />
        <p className="eyebrow">CALIBRATING ATMOSPHERE</p>
        <h1>STORMGLASS</h1>
      </main>
    );
  return <GameContext.Provider value={host}>{children}</GameContext.Provider>;
}

export const useGameHost = (): GameHost => {
  const host = useContext(GameContext);
  if (host === null)
    throw new Error("useGameHost must be used within GameProvider.");
  return host;
};

export const useGameState = (): HostState => {
  const host = useGameHost();
  return useSyncExternalStore(host.subscribe, host.getState, host.getState);
};
